import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = '/api/v1';

export type Period = 'today' | '7days' | '30days' | 'this_month' | 'last_month';
export type Interval = 'hour' | 'day' | 'week';

export interface KpiMetrics {
  processedCalls: { value: number; averageDuration: number; totalDuration: number };
  toolsRun: { value: number; averageProcessingTime: number; totalProcessingTime: number };
}

export interface TimeSeriesData {
  labels: string[];
  datasets: Array<{ label: string; data: number[] }>;
}

export interface UseDashboardMetricsReturn {
  metrics: KpiMetrics | null;
  previousMetrics: KpiMetrics | null;
  timeSeries: TimeSeriesData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  trend: {
    callsPercent: number | null;
    callsDirection: 'up' | 'down' | null;
    toolsPercent: number | null;
    toolsDirection: 'up' | 'down' | null;
  };
}

export function useDashboardMetrics(
  period: Period,
  interval: Interval,
  tenantScope: 'current' | 'all' = 'current'
): UseDashboardMetricsReturn {
  const { isAuthenticated, isGlobalAdmin } = useAuth();
  const [metrics, setMetrics] = useState<KpiMetrics | null>(null);
  const [previousMetrics, setPreviousMetrics] = useState<KpiMetrics | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = useCallback(() => setRefetchCount((n) => n + 1), []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const prevPeriod =
      period === '7days' ? '14days' :
      period === '30days' ? '60days' : period;

    const fetchAll = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params: any = { period };
        const prevParams: any = { period: prevPeriod };
        const tsParams: any = { period, interval };

        // Add tenantScope parameter for global admins
        if (isGlobalAdmin) {
          params.tenantScope = tenantScope;
          prevParams.tenantScope = tenantScope;
          tsParams.tenantScope = tenantScope;
        }

        const [metricsRes, prevMetricsRes, tsRes] = await Promise.all([
          axios.get<KpiMetrics>(`${API_BASE_URL}/dashboard/kpi`, { params }),
          prevPeriod !== period
            ? axios.get<KpiMetrics>(`${API_BASE_URL}/dashboard/kpi`, {
                params: prevParams,
              })
            : Promise.resolve({ data: null }),
          axios.get<TimeSeriesData>(`${API_BASE_URL}/dashboard/timeseries`, {
            params: tsParams,
          }),
        ]);

        setMetrics(metricsRes.data);
        if (prevMetricsRes.data) {
          setPreviousMetrics(prevMetricsRes.data);
        }
        setTimeSeries(tsRes.data);
      } catch (err: any) {
        setError(err.response?.data?.message ?? 'Failed to load dashboard metrics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [isAuthenticated, period, interval, refetchCount, isGlobalAdmin, tenantScope]);

  const trend = {
    callsPercent:
      metrics && previousMetrics && previousMetrics.processedCalls.value > 0
        ? Math.round(
            ((metrics.processedCalls.value - previousMetrics.processedCalls.value) /
              previousMetrics.processedCalls.value) *
              100
          )
        : null,
    callsDirection:
      metrics && previousMetrics
        ? metrics.processedCalls.value >= previousMetrics.processedCalls.value
          ? 'up'
          : 'down'
        : null,
    toolsPercent:
      metrics && previousMetrics && previousMetrics.toolsRun.value > 0
        ? Math.round(
            ((metrics.toolsRun.value - previousMetrics.toolsRun.value) /
              previousMetrics.toolsRun.value) *
              100
          )
        : null,
    toolsDirection:
      metrics && previousMetrics
        ? metrics.toolsRun.value >= previousMetrics.toolsRun.value
          ? 'up'
          : 'down'
        : null,
  };

  return { metrics, previousMetrics, timeSeries, isLoading, error, refetch, trend };
}
